import React, {createRef, ReactNode, useContext} from 'react';
import cx from 'classnames';
import {Icon, PriorityIcon, Flex, noop, Title} from '@priority-software/priority-style-react';
import {common} from '@priority-software/priority-proto-api/lib';
import {Uploader} from './uploader';
import styles from './styles.module.scss';
import {sendMonitor} from '../../utils/monitoring';

export type AttachmentType = common.IFile | null;

export type FileContextModel = {
    file: AttachmentType;
    setFile?: (file: File[]) => Promise<void>;
    deleteFile: () => void;
};

export const FileContext = React.createContext<FileContextModel>({
    file: {Data: null},
    deleteFile: noop
});

type Props = {
    file?: AttachmentType;
    onChange?: (file: AttachmentType, fileName?: string) => void;
    componentClasses?: string;
    children?: ReactNode;
    setFile?: (file: File[]) => Promise<void>;
    title: string;
    monitor?: {send: boolean; text: string};
};

export class FileUploaderController extends React.PureComponent<Props, FileContextModel> {
    dragging: boolean = false;
    ref = createRef<HTMLDivElement>();

    state = {
        file: this.props.file || null,
        setFile: this.props.setFile.bind(this),
        deleteFile: this.deleteFile.bind(this)
    };

    componentDidUpdate(): void {
        if (this.props.file !== this.state.file) {
            this.setState({file: this.props.file});
        }
    }

    deleteFile() {
        this.props.onChange?.({Data: null});
    }

    onDragStart = e => {
        e.preventDefault();

        const dropZoneEl = this.ref.current;
        const isEmpty = !this.state.file?.Data;

        if (dropZoneEl && !this.dragging && isEmpty) {
            dropZoneEl.classList.add(styles.dragStart);
            this.dragging = true;
        }
    };

    onDragLeave = (e?) => {
        e?.preventDefault();
        const dropZoneEl = this.ref.current;

        if (dropZoneEl && this.dragging) {
            setTimeout(() => {
                this.dragging = false;
                dropZoneEl.classList.remove(styles.dragStart);
            }, 200);
        }
    };

    handleDrop = async e => {
        e.preventDefault();
        const {monitor} = this.props;
        if (monitor?.send) {
            sendMonitor(monitor.text);
        }

        this.props.setFile(e?.dataTransfer?.files);
        this.onDragLeave();
    };

    render() {
        return (
            <div
                onDragOver={this.onDragStart}
                ref={this.ref}
                className={cx(styles.dropzoneWrap, this.props.componentClasses)}
            >
                <FileContext.Provider value={this.state}>
                    {this.props.children}
                </FileContext.Provider>

                <div
                    onDrop={this.handleDrop}
                    onDragLeave={this.onDragLeave}
                    className={styles.dropArea}
                >
                    <Title skin="h4" textColor="white" text={this.props.title} />
                </div>
            </div>
        );
    }
}

type ControlProps = {
    label?: ReactNode;
    removeTextLabel?: string;
    placeholder?: string;
    readOnly?: boolean;
    name?: string;
    monitor?: {send: boolean; text: string};
};

export const ImageUploaderControl = ({label, placeholder, readOnly, monitor}: ControlProps) => {
    const {file, setFile, deleteFile} = useContext(FileContext);

    const hasFile = Boolean(file?.Data && file.MimeType);

    return (
        <div className={cx(styles.component, {'uploader_has-file': hasFile})}>
            <Flex componentClasses={styles.labelWrap}>
                {label}
                {!readOnly && file.Data ? (
                    <Icon
                        onClick={deleteFile}
                        componentClasses={styles.removeBtn}
                        icon={PriorityIcon.delete}
                        iconColor="primary-700"
                    />
                ) : null}
            </Flex>

            <Uploader
                componentClasses={styles.dropzone}
                monitor={monitor}
                setFile={setFile}
                placeholder={placeholder}
                file={file}
                readOnly={readOnly}
                accept={'image/*'}
            />
            <div className={styles.uploadedImg}></div>
        </div>
    );
};
